import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { cookieConsentLabels, footerLabels } from "@/i18n";
import AboutDialog from "./about_dialog";
import TermsDialog from "./terms_dialog";
import PrivacyPolicyDialog from "./privacy_policy_dialog";
import { ChevronRight } from "lucide-react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";
import { openCookieConsentSettings } from "@/lib/cookieConsent";

type DialogKey = "about" | "terms" | "privacy";

type LegalPoliciesLinksProps = {
  variant?: "footer" | "settings" | "page";
  showCopyright?: boolean;
  className?: string;
};

export function LegalPoliciesLinks({
  variant = "footer",
  showCopyright = false,
  className,
}: LegalPoliciesLinksProps) {
  const [open, setOpen] = useState<DialogKey | null>(null);
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const labels = footerLabels(language);
  const consentT = cookieConsentLabels(language);

  const items: { key: DialogKey; label: string }[] = [
    { key: "about", label: labels.about },
    { key: "terms", label: labels.terms },
    { key: "privacy", label: labels.privacy },
  ];

  const copyrightText = labels.copyright.replace(
    "{year}",
    String(new Date().getFullYear()),
  );

  const dialogs = (
    <>
      <AboutDialog
        open={open === "about"}
        onOpenChange={(v) => setOpen(v ? "about" : null)}
      />
      <TermsDialog
        open={open === "terms"}
        onOpenChange={(v) => setOpen(v ? "terms" : null)}
      />
      <PrivacyPolicyDialog
        open={open === "privacy"}
        onOpenChange={(v) => setOpen(v ? "privacy" : null)}
      />
    </>
  );

  if (variant === "settings" || variant === "page") {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="rounded-lg border border-border bg-card divide-y divide-border">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setOpen(item.key)}
              className={cn(
                "flex w-full items-center justify-between gap-3 px-4 text-left hover:bg-muted/50 transition-colors",
                variant === "settings" && isNative
                  ? "py-4 text-base native-legal-policy-row"
                  : "py-4 text-base",
              )}
            >
              <span>{item.label}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => openCookieConsentSettings({ edit: true })}
            className={cn(
              "flex w-full items-center justify-between gap-3 px-4 text-left hover:bg-muted/50 transition-colors",
              variant === "settings" && isNative
                ? "py-4 text-base native-legal-policy-row"
                : "py-4 text-base",
            )}
          >
            <span>{consentT.changeSettings}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </div>
        {showCopyright && (
          <p className="text-center text-xs text-muted-foreground pt-2">
            {copyrightText}
          </p>
        )}
        {dialogs}
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-2 place-items-center md:flex md:flex-row items-center justify-between w-full md:w-[85%] gap-4 md:gap-0",
          className,
        )}
      >
        {showCopyright && (
          <div className="text-center w-full">
            <p className="text-primary/70 text-xs md:text-sm">{copyrightText}</p>
          </div>
        )}
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            className="text-primary/70 hover:underline text-xs md:text-sm text-center w-full md:pl-12"
            onClick={() => setOpen(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {dialogs}
    </>
  );
}
